import { useEffect, useState } from "react";

import type { ConnectionReport, PolyfutureApiClient } from "./api.js";

export interface ConnectionState {
  online: boolean;
  /** When the server last answered a request. Undefined until the first answer. */
  lastReachedAt?: number;
}

const probeIntervalMs = 15_000;
const clockIntervalMs = 30_000;

/**
 * Tracks whether the server can be reached, from the browser's own online signal and from every
 * API request's outcome. While unreachable, it probes every 15 seconds until the server answers.
 */
export function useConnectionState(client: PolyfutureApiClient): ConnectionState {
  const [state, setState] = useState<ConnectionState>(() => ({
    online: typeof navigator === "undefined" ? true : navigator.onLine,
  }));
  const [, setClock] = useState(0);

  useEffect(
    () =>
      client.onConnection((report: ConnectionReport) =>
        setState((current) => nextConnectionState(current, report)),
      ),
    [client],
  );

  useEffect(() => {
    const goOffline = () => setState((current) => ({ ...current, online: false }));
    const goOnline = () => void client.probeConnection();
    window.addEventListener("offline", goOffline);
    window.addEventListener("online", goOnline);
    return () => {
      window.removeEventListener("offline", goOffline);
      window.removeEventListener("online", goOnline);
    };
  }, [client]);

  useEffect(() => {
    if (state.online) return;
    const timer = window.setInterval(() => void client.probeConnection(), probeIntervalMs);
    return () => window.clearInterval(timer);
  }, [client, state.online]);

  // Re-render periodically so the age in the top bar stays true while nothing is requested.
  useEffect(() => {
    const timer = window.setInterval(() => setClock((tick) => tick + 1), clockIntervalMs);
    return () => window.clearInterval(timer);
  }, []);

  return state;
}

export function nextConnectionState(
  current: ConnectionState,
  report: ConnectionReport,
): ConnectionState {
  return report.reachable
    ? { online: true, lastReachedAt: report.at }
    : {
        online: false,
        ...(current.lastReachedAt === undefined ? {} : { lastReachedAt: current.lastReachedAt }),
      };
}

/** The top-bar wording. It names the connection, not market data, which has no feed yet. */
export function connectionLabel(state: ConnectionState, now: number): string {
  if (!state.online) return "Reconnecting";
  if (state.lastReachedAt === undefined) return "Connecting";
  const minutes = Math.floor((now - state.lastReachedAt) / 60_000);
  if (minutes < 1) return "Connected";
  if (minutes < 60) return `Connected · ${minutes}m ago`;
  return `Connected · ${Math.floor(minutes / 60)}h ago`;
}

export function TopbarConnection({ state }: { state: ConnectionState }) {
  return (
    <span className={state.online ? "topbar-live" : "topbar-live offline"} role="status">
      {connectionLabel(state, Date.now())}
    </span>
  );
}

export function OfflineBanner({ state }: { state: ConnectionState }) {
  if (state.online) return null;
  const since =
    state.lastReachedAt === undefined
      ? "Nothing has been received yet."
      : `Showing the last values received as of ${new Date(state.lastReachedAt).toLocaleTimeString(
          [],
          { hour: "numeric", minute: "2-digit" },
        )}.`;
  return (
    <div className="offline-banner" role="alert">
      <span className="offline-banner-dot" aria-hidden="true" />
      Reconnecting. {since} Nothing is being published while offline.
    </div>
  );
}
